/**
 * Kimmo's browser results: final time, PB comparison, the split table, and what became
 * of the submission.
 *
 * The submission line is deliberately wordy. "Unranked" is a thing that can
 * happen for perfectly innocent reasons - no name set, no network, an expired
 * ticket - and a player who has just run a personal best deserves to be told
 * which one it was rather than left staring at a silent board.
 */

import { describeIntoxication } from '../config/intoxication';
import { formatDelta, formatSplit, formatTime } from '../utils/format';
import type { FinishedRun } from '../game/RunManager';
import type { RunResult } from '../score/ScoreRepository';
import type { SubmissionState } from '../score/RunSubmitter';
import { LeaderboardPanel } from './LeaderboardPanel';
import { MenuButtons } from './MenuButtons';
import { button, el, setHidden, setText } from './dom';
import { TaskCheckbox } from './TaskCheckbox';

export interface FinishScreenActions {
  restart: () => void;
  returnToMenu: () => void;
}

export class FinishScreen {
  /**
   * Not clickthrough, unlike the other read-only overlays: the run is over, the
   * mouse has been handed back (see `Game.onFinished`), and a stray click that
   * fell through to the canvas would silently take it away again - along with
   * the buttons under it.
   */
  readonly root = el('div', 'overlay finish-page');
  readonly board = new LeaderboardPanel('TULOSTAULU');
  /** Menu actions for the arrows and Enter. See `Game.keyboardMenu`. */
  readonly buttons: MenuButtons;

  private readonly time = el('div', 'overlay__time', '00:00.000');
  private readonly dose = el('p', 'overlay__dose');
  private readonly pbFlash = el('div', 'overlay__pb-flash', 'UUSI ENNÄTYS!');
  private readonly delta = el('div', 'overlay__delta');
  private readonly best = el('p', 'overlay__line');
  private readonly splits = el('ul', 'overlay__splits');
  private readonly submission = el('p', 'overlay__submit');

  constructor(actions: FinishScreenActions) {
    this.root.addEventListener('mousedown', (event) => event.stopPropagation());

    // This screen opens only after the PIN succeeds, not when Kimmo sits down.
    const objective = el('div', 'hud__objective-main hud__objective--complete');
    const check = new TaskCheckbox();
    check.update(true);
    objective.append(check.root, el('span', '', 'Tehtävä: Siirry omalle paikallesi'));

    const columns = el('div', 'overlay__columns');
    const left = el('div', 'overlay__column');
    left.append(this.splits);
    const right = el('div', 'overlay__column');
    right.append(this.board.root);
    columns.append(left, right);

    const nodes = [
      button('UUSI YRITYS', () => actions.restart(), 'btn btn--primary', 'R'),
      button('PALAA PÄÄVALIKKOON', () => actions.returnToMenu()),
    ];
    this.buttons = new MenuButtons(nodes);
    this.board.onButtonsChanged = (rows) => this.buttons.setButtons([...nodes, ...rows]);
    const row = el('div', 'menu__actions menu__actions--row');
    row.append(...nodes);

    this.root.append(
      el('p', 'menu__eyebrow', 'LANIAAMU / TULOKSET'),
      objective,
      el('h1', 'overlay__title', 'MAALI'),
      this.time,
      this.dose,
      this.pbFlash,
      this.delta,
      this.best,
      this.submission,
      columns,
      row,
    );
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  /**
   * Where the run stands with the server. Called on every state change, so a
   * player watching the screen sees "lähetetään..." turn into a rank.
   */
  showSubmission(state: SubmissionState, hasName: boolean): void {
    const [text, modifier] = describeSubmission(state, hasName);
    setText(this.submission, text);
    this.submission.className = modifier
      ? `overlay__submit overlay__submit--${modifier}`
      : 'overlay__submit';
    setHidden(this.submission, text === '');
  }

  show(run: FinishedRun, personalBest: RunResult | null): void {
    setText(this.time, formatTime(run.result.timeMs));
    setText(this.dose, describeIntoxication(run.result.intoxication));

    const isPb = run.comparison.isPersonalBest;
    setHidden(this.pbFlash, !isPb);

    if (run.comparison.deltaMs === null) {
      setText(this.delta, 'ENSIMMÄINEN YRITYS');
      this.delta.className = 'overlay__delta';
    } else {
      setText(this.delta, formatDelta(run.comparison.deltaMs));
      this.delta.className =
        'overlay__delta ' +
        (run.comparison.deltaMs <= 0 ? 'overlay__delta--good' : 'overlay__delta--bad');
    }

    setText(this.best, personalBest ? `ENNÄTYS  ${formatTime(personalBest.timeMs)}` : '');

    this.splits.replaceChildren();
    let previous = 0;
    for (const split of run.result.splits) {
      const row = el('li');
      row.append(
        el('span', undefined, split.label),
        el('span', undefined, `${formatSplit(split.timeMs)}   (+${formatSplit(split.timeMs - previous)})`),
      );
      this.splits.appendChild(row);
      previous = split.timeMs;
    }
  }

  setVisible(visible: boolean): void {
    setHidden(this.root, !visible);
    this.buttons.setActive(visible);
  }
}

type SubmitTone = 'good' | 'bad' | 'muted' | '';

function describeSubmission(state: SubmissionState, hasName: boolean): [string, SubmitTone] {
  switch (state.status) {
    case 'offline':
      return ['Tulostaulu ei ole käytettävissä - tulos jää tälle koneelle.', 'muted'];
    case 'idle':
      return hasName
        ? ['', '']
        : ['Aseta nimimerkki asetuksista, jos haluat tuloksesi tulostauluun.', 'muted'];
    case 'unranked':
      return [`Ei tulostauluun: ${state.reason}`, 'muted'];
    case 'sending':
      return ['Lähetetään ja validoidaan palvelimella...', 'muted'];
    case 'accepted':
      return state.flagged
        ? ['Tulos tallennettu, mutta merkitty tarkistettavaksi.', 'bad']
        : [`Vahvistettu tulostauluun - sija ${state.rank}.`, 'good'];
    case 'rejected':
      return [`Palvelin hylkäsi tuloksen: ${state.reason}`, 'bad'];
    case 'error':
      return [`Lähetys epäonnistui: ${state.reason}`, 'bad'];
  }
}
